import type {
    CommerceAddress,
    CommercePaymentAdapter,
    CommercePaymentAdapterEventName,
    CommercePaymentAdapterListener,
    CommercePaymentAdapterResult,
} from "@teqtank/commerce-contracts";

export interface PaymentHandlerOptions {
    readonly frameId?: string;
    readonly frameUri?: string | readonly string[];
    readonly redirectLoader?: string;
    readonly autoLoader?: boolean;
    readonly debug?: boolean;
    /** Styling forwarded to PaymentHandler for its blocking modals and overlays. */
    readonly styles?: Readonly<Record<string, unknown>>;
}

/** Provider-specific billing shape accepted by the current PaymentHandler UMD. */
export interface PaymentHandlerBillingAddress {
    readonly street: string;
    readonly city: string;
    readonly region: string;
    readonly postal_code: string;
    readonly country: string;
}

export interface PaymentHandlerInstance {
    createOrder(order: unknown): Promise<unknown>;
    setBilling(address: PaymentHandlerBillingAddress): void;
    clearBilling(): void;
    setPaymentWindow(): void;
    unsetPaymentWindow(): void;
    on(event: CommercePaymentAdapterEventName, callback: CommercePaymentAdapterListener): void;
    off(event: CommercePaymentAdapterEventName, callback: CommercePaymentAdapterListener): void;
    dispose(): void;
}

export interface PaymentHandlerConstructor {
    new (options: PaymentHandlerOptions): PaymentHandlerInstance;
}

export interface TeqtankPaymentHandlerAdapterOptions extends PaymentHandlerOptions {
    readonly handler?: PaymentHandlerConstructor;
    /** Direct iframe reference used by the adapter when Web Components place it behind Shadow DOM. */
    readonly frame?: HTMLIFrameElement;
}

interface PaymentHandlerRuntimeOptions extends PaymentHandlerOptions {
    readonly customModalStyles?: Readonly<Record<string, unknown>>;
    readonly customFrameStyles?: Readonly<Record<string, unknown>>;
}

interface PaymentHandlerRuntimeInstance extends PaymentHandlerInstance {
    _postMessage?: (data: unknown) => void;
}

declare global {
    interface Window {
        PaymentHandler?: PaymentHandlerConstructor;
    }
}

/** Strongly typed SDK boundary around the externally loaded UMD PaymentHandler. */
export class TeqtankPaymentHandlerAdapter implements CommercePaymentAdapter {
    public readonly name = "teqtank-payment-handler";
    private readonly handler: PaymentHandlerRuntimeInstance;
    private readonly removeFrameMessageBridge: (() => void) | undefined;

    public constructor(options: TeqtankPaymentHandlerAdapterOptions) {
        const { handler: suppliedHandler, frame, styles, ...paymentHandlerOptions } = options;
        const Handler = suppliedHandler ?? globalThis.window?.PaymentHandler;
        if (Handler === undefined) throw new Error("PaymentHandler UMD has not been loaded.");
        if (options.frameId === undefined || options.frameId.trim().length === 0)
            throw new Error("A payment iframe frameId is required.");
        const allowedOrigins = normalizeOrigins(paymentHandlerOptions.frameUri);
        const runtimeOptions: PaymentHandlerRuntimeOptions = {
            ...paymentHandlerOptions,
            frameUri: allowedOrigins.length === 1 ? allowedOrigins[0]! : allowedOrigins,
            ...(styles === undefined
                ? {}
                : { styles, customModalStyles: styles, customFrameStyles: styles }),
        };
        this.handler = new Handler(runtimeOptions) as PaymentHandlerRuntimeInstance;
        this.removeFrameMessageBridge =
            frame === undefined
                ? undefined
                : installShadowDomFrameBridge(this.handler, frame, allowedOrigins);
    }

    public async createOrder(order: unknown): Promise<CommercePaymentAdapterResult> {
        return Object.freeze({
            response: await this.handler.createOrder(order),
        });
    }

    public setBilling(address: CommerceAddress): void {
        this.handler.setBilling({
            street: address.addressLine1,
            city: address.city,
            region: address.region,
            postal_code: address.postalCode,
            country: address.countryCode,
        });
    }
    public clearBilling(): void {
        this.handler.clearBilling();
    }
    public setPaymentWindow(): void {
        this.handler.setPaymentWindow();
    }
    public unsetPaymentWindow(): void {
        this.handler.unsetPaymentWindow();
    }
    public on(
        event: CommercePaymentAdapterEventName,
        listener: CommercePaymentAdapterListener,
    ): void {
        this.handler.on(event, listener);
    }
    public off(
        event: CommercePaymentAdapterEventName,
        listener: CommercePaymentAdapterListener,
    ): void {
        this.handler.off(event, listener);
    }
    public dispose(): void {
        this.removeFrameMessageBridge?.();
        this.handler.dispose();
    }
}

function installShadowDomFrameBridge(
    handler: PaymentHandlerRuntimeInstance,
    frame: HTMLIFrameElement,
    allowedOrigins: readonly string[],
): () => void {
    handler._postMessage = (data: unknown): void => {
        const sourceOrigin = new URL(frame.src).origin;
        if (!allowedOrigins.includes(sourceOrigin)) {
            console.warn("No matching origin for payment postMessage");
            return;
        }
        frame.contentWindow?.postMessage(makeCloneablePaymentMessage(data), sourceOrigin);
    };

    const receive = (event: MessageEvent): void => {
        if (
            event.source !== frame.contentWindow ||
            !allowedOrigins.includes(event.origin) ||
            !isResizeMessage(event.data)
        )
            return;
        frame.setAttribute("height", String(event.data.height));
    };
    globalThis.window.addEventListener("message", receive, false);
    return () => {
        globalThis.window.removeEventListener("message", receive, false);
    };
}

function makeCloneablePaymentMessage(data: unknown): unknown {
    if (typeof data === "object" && data !== null) {
        const diagnostic = data as {
            readonly type?: unknown;
            readonly code?: unknown;
            readonly data?: unknown;
        };
        if (diagnostic.type === "log" && diagnostic.data instanceof MessageEvent) {
            return {
                type: "log",
                ...(typeof diagnostic.code === "string" ? { code: diagnostic.code } : {}),
                data: {
                    type: diagnostic.data.type,
                    origin: diagnostic.data.origin,
                },
            };
        }
    }
    try {
        structuredClone(data);
        return data;
    } catch {
        if (typeof data !== "object" || data === null) return data;
        const message = data as {
            readonly type?: unknown;
            readonly code?: unknown;
            readonly data?: unknown;
        };
        if (message.type !== "log") {
            throw new Error("The payment library produced a non-cloneable message.");
        }
        const event = message.data;
        return {
            type: "log",
            ...(typeof message.code === "string" ? { code: message.code } : {}),
            data:
                event instanceof MessageEvent
                    ? { type: event.type, origin: event.origin }
                    : { omitted: "non-cloneable diagnostic payload" },
        };
    }
}

function normalizeOrigins(configured: string | readonly string[] | undefined): readonly string[] {
    const values =
        configured === undefined
            ? [
                  "https://tts.teqtank.com",
                  "https://stagingtts.teqtank.com",
                  "https://devtts.teqtank.com",
              ]
            : typeof configured === "string"
              ? [configured]
              : configured;
    return Object.freeze([...new Set(values.map((value) => new URL(value).origin))]);
}

function isResizeMessage(
    value: unknown,
): value is { readonly type: "resize"; readonly height: string | number } {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as {
        readonly type?: unknown;
        readonly height?: unknown;
    };
    if (candidate.type !== "resize") return false;
    if (typeof candidate.height === "number")
        return Number.isFinite(candidate.height) && candidate.height >= 0;
    return typeof candidate.height === "string" && /^\d+(?:\.\d+)?$/.test(candidate.height);
}

export interface MockCommercePaymentAdapterOptions {
    readonly delayMilliseconds?: number;
    readonly shouldFail?: boolean;
    readonly failureMessage?: string;
}

/** Demo/test adapter used until the Commerce Gateway payment endpoint is connected. */
export class MockCommercePaymentAdapter implements CommercePaymentAdapter {
    public readonly name = "mock-payment";
    private readonly listeners = new Map<
        CommercePaymentAdapterEventName,
        Set<CommercePaymentAdapterListener>
    >();
    private disposed = false;
    private billingAddress: CommerceAddress | null = null;

    public constructor(private readonly options: MockCommercePaymentAdapterOptions = {}) {}

    public async createOrder(order: unknown): Promise<CommercePaymentAdapterResult> {
        if (this.disposed) throw new Error("Payment adapter has been disposed.");
        await delay(this.options.delayMilliseconds ?? 80);
        if (this.options.shouldFail === true) {
            const response = Object.freeze({
                response: this.options.failureMessage ?? "The demo payment was declined.",
            });
            this.emit("error", response);
            throw response;
        }
        const source =
            typeof order === "object" && order !== null
                ? (order as Readonly<Record<string, unknown>>)
                : (Object.freeze({}) as Readonly<Record<string, unknown>>);
        const checkoutId =
            typeof source.checkoutId === "string" ? source.checkoutId : "mock-checkout";
        const response = Object.freeze({
            type: "submit-response",
            checkoutId,
            orderId: `demo-${checkoutId}`,
            paymentStatus: "captured",
            billingAddress: this.billingAddress,
        });
        this.emit("success", response);
        return Object.freeze({ response });
    }

    public setBilling(address: CommerceAddress): void {
        this.billingAddress = address;
    }
    public clearBilling(): void {
        this.billingAddress = null;
    }
    public setPaymentWindow(): void {}
    public unsetPaymentWindow(): void {}
    public on(
        event: CommercePaymentAdapterEventName,
        listener: CommercePaymentAdapterListener,
    ): void {
        const set = this.listeners.get(event) ?? new Set();
        set.add(listener);
        this.listeners.set(event, set);
    }
    public off(
        event: CommercePaymentAdapterEventName,
        listener: CommercePaymentAdapterListener,
    ): void {
        this.listeners.get(event)?.delete(listener);
    }
    public dispose(): void {
        this.disposed = true;
        this.listeners.clear();
        this.billingAddress = null;
    }
    private emit(event: CommercePaymentAdapterEventName, data: unknown): void {
        this.listeners.get(event)?.forEach((listener) => listener(data));
    }
}

export class UnconfiguredPaymentAdapter implements CommercePaymentAdapter {
    public readonly name = "unconfigured";
    public createOrder(_order: unknown): Promise<CommercePaymentAdapterResult> {
        return Promise.reject(new Error("Payment is not configured."));
    }
    public setBilling(_address: CommerceAddress): void {}
    public clearBilling(): void {}
    public dispose(): void {}
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}
